import {
  DocType,
  EventDoc,
  EventRecurrence,
  EventStatus,
  Exclusion
} from 'types/api/changes'

import { Frequency, Options, RRule, RRuleSet, rrulestr } from 'rrule'

import Database from 'services/db/Database'
import { isTrulyEmpty } from 'utils/object'

import { is24meApp } from 'utils/appType'

import dayjs, { Dayjs } from 'dayjs'

export function generateGroupcalArrayFromRecurrence(
  groupcalEvent: EventDoc,
  ignoreExclusions = false,
  from?: Dayjs,
  to?: Dayjs,
  defaultTz?: string
): EventDoc[] {
  if (!defaultTz) {
    defaultTz = Intl.DateTimeFormat().resolvedOptions().timeZone
  }

  const instancesFrom =
    from ?? dayjs().tz(defaultTz).subtract(1, 'week').startOf('day')
  const instancesTo = to ?? dayjs().tz(defaultTz).startOf('day').add(50, 'day')

  if (!groupcalEvent.Recurrence || typeof groupcalEvent.Recurrence === 'string')
    return [groupcalEvent]
  //return itself in
  if (groupcalEvent._id?.length === 0) return [groupcalEvent]
  try {
    const durInSeconds =
      Number(groupcalEvent.EndDate) - Number(groupcalEvent.StartDate)
    const rruleString = mapRecurrency(
      groupcalEvent,
      true,
      ignoreExclusions,
      defaultTz
    )

    if (isTrulyEmpty(rruleString)) {
      return [groupcalEvent]
    }
    const rrule = rrulestr(rruleString)

    let instances = rrule.between(instancesFrom.toDate(), instancesTo.toDate())
    const endRecurrence = groupcalEvent.Recurrence?.EndDate
    // we need to remove instances which are after the end date
    if (endRecurrence && !isTrulyEmpty(endRecurrence)) {
      const endRecurrenceDate = dayjs(Number(endRecurrence) * 1000)
      instances = instances.filter((instance) => {
        return (
          groupcalEvent.AllDay === '1' ? dayjs.utc(instance) : dayjs(instance)
        ).isBefore(
          (groupcalEvent.AllDay === '1'
            ? dayjs.utc(endRecurrenceDate)
            : dayjs(endRecurrenceDate).tz(defaultTz ?? 'UTC')
          ).endOf('day')
        )
      })
    }
    // Adjust each instance by the timezone offset
    const instancesWithAdjustedTime = instances.map((instance) => {
      // Get the timezone offset in minutes
      let timezoneOffset = dayjs(instance).utcOffset()
      if (groupcalEvent.AllDay === '1') {
        timezoneOffset = 0
      }
      const instanceInUTC = dayjs(instance).utc()
      return instanceInUTC.subtract(timezoneOffset, 'minute').valueOf()
    })

    return instancesWithAdjustedTime.map((instance) => {
      const eventDoc = Database.toServerObject(groupcalEvent)
      const copyEvent: EventDoc = { ...groupcalEvent, ...eventDoc }
      let tz =
        (eventDoc.TimeZoneNameID?.length ?? 0) > 0
          ? eventDoc.TimeZoneNameID
          : defaultTz
      if (
        is24meApp() &&
        copyEvent.AllDay === '1' &&
        copyEvent.TimeZoneNameID?.length === 0
      ) {
        tz = 'UTC'
      }
      copyEvent.StartDate = (
        dayjs(instance.valueOf()).tz(tz).valueOf() / 1000
      ).toString()
      copyEvent.EndDate = (
        dayjs(instance.valueOf()).tz(tz).add(durInSeconds, 'second').valueOf() /
        1000
      ).toString()
      if (typeof copyEvent.Recurrence?.Exclusion !== 'string') {
        copyEvent.Status =
          eventDoc.Status !== EventStatus.ACTIVE
            ? eventDoc.Status
            : String(
                findStatusForInstance(
                  copyEvent,
                  Number(copyEvent.StartDate) * 1000,
                  defaultTz
                )
              )
      }
      return copyEvent
    })
  } catch (e) {
    console.log(groupcalEvent)
    console.log(instancesFrom)
    console.log(instancesTo)
    console.error(e)
    return [groupcalEvent]
  }
}

export function findStatusForInstance(
  copyEvent: EventDoc,
  instanceTime: number,
  defaultTz?: string
) {
  let maxStatus = Number(EventStatus.ACTIVE)
  const tz =
    (copyEvent.TimeZoneNameID?.length ?? 0) > 0
      ? copyEvent.TimeZoneNameID
      : defaultTz

  for (const exclusion of copyEvent.Recurrence?.Exclusion ?? []) {
    const dayjsExclusion = dayjs(Number(exclusion.Date) * 1000).startOf('day')

    const dayjsEvent = dayjs(instanceTime).tz(tz)

    if (
      dayjsExclusion.format('YYYY-MM-DD') === dayjsEvent.format('YYYY-MM-DD')
    ) {
      if (Number(exclusion.Status) > maxStatus) {
        maxStatus = Number(exclusion.Status)
      }
    }
  }

  return maxStatus
}

/**
 * converts groupcal recurrence to the rfc5545 rrule, see {@link https://www.rfc-editor.org/rfc/rfc5545#section-3.3.10}
 * {@link https://www.rfc-editor.org/rfc/rfc5545#section-3.8.5}
 * @param event 'groupcal' model of the event
 * @param keepDTStart flag which determines DTSTART field of RRULE
 * @returns string representation of a 'groupcal' recurrence
 */
export function mapRecurrency(
  event: EventDoc,
  keepDTStart: boolean,
  ignoreExclusions = false,
  defaultTz?: string
): string {
  let options: Partial<Options> | undefined = undefined
  const recurrence: EventRecurrence | undefined = event.Recurrence
  const rruleSet: RRuleSet = new RRuleSet()

  if (recurrence && recurrence.Interval) {
    options = {
      interval: recurrence.Interval
    }

    if (recurrence.Unit === 5) {
      options.byweekday = RRule.FR.nth(recurrence.Interval)
    }

    if (recurrence.Unit) {
      options.freq = groupCalFrequencyToGoogle(Number(recurrence.Unit))
    }

    rruleSet.rrule(new RRule(options))

    let rruleForEvent = rruleSet.toString()

    let eventTz = isTrulyEmpty(event.TimeZoneNameID)
      ? defaultTz
      : event.TimeZoneNameID

    const eventStart = dayjs
      .unix(Number(event.StartDate))
      .tz(
        event.AllDay === '1'
          ? 'UTC'
          : (event.TimeZoneNameID?.length ?? 0) > 0
            ? event.TimeZoneNameID
            : defaultTz
      )

    if (keepDTStart) {
      /**
       * In order to preserve original start time of recurring event between daylight savings, we need to set
       * DTSTART and UNTIL manually, so event will not jump in hours on view
       */
      let validStart = `DTSTART;TZID=${isTrulyEmpty(event.TimeZoneNameID) ? defaultTz : eventTz}:${eventStart.format(`YYYYMMDDTHHmmss`)}\n\r`

      if (
        (event.Type === DocType.TASK || event.Type === DocType.EVENT) &&
        event.AllDay === '1' &&
        (event.TimeZoneNameID?.length === 0 || event.TimeZoneNameID !== 'UTC')
      ) {
        validStart = `DTSTART:${eventStart
          .add(13, 'hour')
          .format('YYYYMMDDTHHmmss')}\n\r`
      }

      rruleForEvent = rruleForEvent.padStart(
        rruleForEvent.length + validStart.length,
        validStart
      )
    }

    if (recurrence?.EndDate && !isNaN(+recurrence?.EndDate)) {
      let endRecur = dayjs

        .unix(Number(recurrence?.EndDate))

        .tz(defaultTz)
        .endOf('day')
        .tz('UTC')

      if (event.AllDay === '1') {
        endRecur = dayjs

          .unix(Number(recurrence?.EndDate))

          .tz('UTC')
      }

      const validEnd = `;UNTIL=${endRecur.format(
        'YYYYMMDDTHHmmss'
      )}${keepDTStart ? '' : 'Z'}`

      rruleForEvent = rruleForEvent.padEnd(
        rruleForEvent.length + validEnd.length,
        validEnd
      )
    }
    if (
      event?.Recurrence?.Exclusion &&
      Array.isArray(event?.Recurrence?.Exclusion) &&
      !ignoreExclusions
    ) {
      event.Recurrence?.Exclusion?.forEach((excl: Exclusion) => {
        try {
          if (
            !isNaN(Number(excl.Date)) &&
            excl.Status !== EventStatus.COMPLETED &&
            excl.Status !== EventStatus.ACTIVE
          ) {
            const tz =
              (event.TimeZoneNameID?.length ?? 0) > 0
                ? event.TimeZoneNameID
                : defaultTz
            rruleForEvent = `${rruleForEvent}\n\rEXDATE:${
              excl.ExclusionDate && event.AllDay !== '1'
                ? dayjs
                    .tz(excl.ExclusionDate, 'YYYYMMDDTHHmmss', defaultTz)
                    .format('YYYYMMDDTHHmmss')
                : dayjs
                    .unix(Number(excl.Date))
                    .set('hour', eventStart.hour())
                    .set('minute', eventStart.minute())
                    .format('YYYYMMDDTHHmmss')
            }`
          }
        } catch (e) {
          console.error(e)
        }
      })
    }

    return rruleForEvent
  }

  return rruleSet.toString()
}

export function groupCalFrequencyToGoogle(freq: Number): Frequency {
  switch (freq) {
    case 1:
      return Frequency.DAILY
    case 2:
      return Frequency.WEEKLY
    case 3:
      return Frequency.MONTHLY
    case 4:
      return Frequency.YEARLY
  }

  return Frequency.DAILY
}
